import { useCallback, useEffect, useState } from "react";
import {
  fetchCognitoryWorkspaceCapabilities,
  type CognitoryWorkspaceCapabilities,
} from "../api/cognitoryWorkspaceCapabilitiesClient";

export type CognitoryWorkspaceCapabilitiesPhase =
  | "idle"
  | "loading"
  | "ready"
  | "unauthenticated"
  | "forbidden"
  | "temporary_failure"
  | "malformed";

export function useCognitoryWorkspaceCapabilities(input: {
  cognitoryId: string | null;
  enabled: boolean;
}) {
  const [phase, setPhase] =
    useState<CognitoryWorkspaceCapabilitiesPhase>("idle");
  const [capabilities, setCapabilities] =
    useState<CognitoryWorkspaceCapabilities | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (!input.enabled || !input.cognitoryId) {
      setPhase("idle");
      setCapabilities(null);
      setRequestId(null);
      return;
    }
    let cancelled = false;
    setPhase("loading");
    void fetchCognitoryWorkspaceCapabilities({
      cognitoryId: input.cognitoryId,
    }).then(result => {
      if (cancelled) {
        return;
      }
      setRequestId(result.requestId);
      if (result.kind === "resolved") {
        setCapabilities(result.capabilities);
        setPhase("ready");
        return;
      }
      if (result.kind === "unauthenticated") {
        setPhase("unauthenticated");
        return;
      }
      if (result.kind === "forbidden") {
        setPhase("forbidden");
        return;
      }
      if (result.kind === "malformed_success") {
        setPhase("malformed");
        return;
      }
      setPhase("temporary_failure");
    });
    return () => {
      cancelled = true;
    };
  }, [input.cognitoryId, input.enabled, reloadToken]);

  return { phase, capabilities, requestId, reload };
}
